import React, { Component } from 'react';
import { INTERVAL, TYPE_SUCCESS } from 'assets/constants/Constants';
import Spinner from 'components/Spinner';
import {
    PLUS_ICON,
    ARROW_DOWN_ICON,
    ARROW_UP_ICON,
    TRASH_ICON,
    FILE_ICON,
    ALIGNJUSTIFY_ICON,
    STEPS_ICON,
} from 'assets/constants/Icons';
import CreatableSelect from 'react-select/creatable';
import ShowToast from 'components/ShowToast';
import moment from 'moment';
import { getStepList, createStdProcess, updateStdProcess, createStep } from 'lib/axiosUtils';
import 'assets/css/stdProcess.css';

const isBrowser = typeof window !== 'undefined';

function getId(s, idx = 0) {
    return s?._id?.$oid ?? s?._id ?? s?.id ?? `idx_${idx}`;
}

function getTitle(s, idx = 0) {
    return (
        s?.step_title_snapshot ??
        s?.form_schema?.title ??
        s?.form_schema?.properties?.title ??
        s?.step_title ??
        `Untitled Step ${idx + 1}`
    );
}

function normalizeOptions(raw) {
    const arr = Array.isArray(raw) ? raw : raw ? [raw] : [];
    return arr
        .map((s, idx) => {
            const value = getId(s, idx);
            const label = String(getTitle(s, idx));
            if (value === undefined || value === null) return null;
            return { value, label, meta: s };
        })
        .filter(Boolean);
}

export class CreateStdProcessPage extends Component {
    constructor(props) {
        super(props);

        const editData =
            props && props.location && props.location.state ? props.location.state : null;

        const preSelected = (editData && (editData.process_steps || editData.processSteps || editData.steps)) || [];

        this.state = {
            isLoading: true,
            processId: editData ? editData._id ?? editData.processId ?? null : null,
            processTitle: editData ? editData.process_title ?? editData.processTitle ?? '' : '',
            numberOfStepsText: editData
                ? String(
                      editData.number_of_steps ??
                          editData.numberOfSteps ??
                          ''
                  )
                : '',
            stepListComponent: [],
            selectedSteps:
                preSelected.length > 0
                    ? preSelected.map((s, i) => {
                          const value =
                              s?.step ??
                              (typeof s === 'string' || typeof s === 'number' ? s : `pref_${i}`);
                          const label =
                              s?.step_title_snapshot ?? // This is the correct snapshot title
                              s?.step?.step_title ??
                              s?.stepTitle ??
                              s?.label ??
                              (typeof s === 'string' || typeof s === 'number'
                                  ? String(s)
                                  : `Step ${i + 1}`);
                          
                          const duration = s?.duration_days ?? 1; 
                          
                          return { value, label, meta: s, durationDays: duration };
                      })
                    : [{ value: '', label: '', meta: {}, durationDays: 1 }],
            dragItem: null,
            dragOverItem: null,
            flag: false,
            saveFlag: false,
            formSchema: editData?.form_schema || {},
            uiSchema: editData?.ui_schema || {},
            error: {},
            editData,
        };
    }

    async componentDidMount() {
        try {
            const res = await getStepList({}, {});
            const raw = res?.data?.result ?? res?.data?.steps ?? res?.data ?? res ?? [];
            const stepListComponent = normalizeOptions(raw);
            this.setState({
                stepListComponent,
                isLoading: false,
            });
        } catch (err) {
            console.error('[CreateStdProcessPage] error loading steps:', err);
            this.setState({ isLoading: false });
        }
    }

    setProcessTitle = (value) => this.setState({ processTitle: value });

    addStep = () => {
        const selectedSteps = [...this.state.selectedSteps, { value: '', label: '', meta: {}, durationDays: 1 }];
        this.setState({ selectedSteps });
    };

    removeStep = (index) => {
        const arr = [...this.state.selectedSteps];
        if (arr.length === 1) {
            window.alert('Minimum one step is required');
            return;
        }
        arr.splice(index, 1);
        this.setState({ selectedSteps: arr });
    };

    setStepSelect = (option, index) => {
        const arr = [...this.state.selectedSteps];   
        const duration = option?.meta?.predefined_duration_days ?? 1;
        arr[index] = {
            value: option?.value ?? '',
            label: option?.label ?? '',
            meta: option?.meta ?? {},
            durationDays: duration,
        };
        this.setState({ selectedSteps: arr });
    };

    setDuration = (index, value) => {
        const arr = [...this.state.selectedSteps];
        const v = Number(value);
        arr[index].durationDays = Number.isFinite(v) && v>=0 ? Math.floor(v) : 0;
        this.setState({ selectedSteps: arr });
    };

    arrowDown = (index) => {
        const arr = [...this.state.selectedSteps];
        if (arr.length > 1 && index + 1 < arr.length) {
            [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
            this.setState({ selectedSteps: arr });
        }
    };

    arrowUp = (index) => {
        const arr = [...this.state.selectedSteps];
        if (arr.length > 1 && index !== 0) {
            [arr[index], arr[index - 1]] = [arr[index - 1], arr[index]];
            this.setState({ selectedSteps: arr });
        }
    };

    dragStart = (e, position) => this.setState({ dragItem: position });
    dragEnter = (e, position) => this.setState({ dragOverItem: position });

    drop = () => {
        const { dragItem, dragOverItem, selectedSteps } = this.state;
        if (dragItem == null || dragOverItem == null) {
            this.setState({ dragItem: null, dragOverItem: null });
            return;
        }
        const arr = [...selectedSteps];
        const dragItemContent = arr[dragItem];
        arr.splice(dragItem, 1);
        arr.splice(dragOverItem, 0, dragItemContent);
        this.setState({ selectedSteps: arr, dragItem: null, dragOverItem: null });
    };

    validate = () => {
        let isValid = true;
        let error = {};
        if (!this.state.processTitle || this.state.processTitle.trim() === '') {
            isValid = false;
            error.stepTitle = 'Please enter process title';
        }
        this.state.selectedSteps.forEach((s, idx) => {
            if (!s || !s.value) {
                isValid = false;
                error[`selectedSteps_${idx}`] = 'Please select a step';
            }
        });
        if (this.state.selectedSteps.length === 0) {
            isValid = false;
            error.selectedStep = 'Please add at least one step';
        }
        this.setState({ error });
        return isValid;
    };

    saveData = () => {
        if (this.validate()) {
            this.setState({ saveFlag: true }, this.generateAndSaveData);
        }
    };

    generateAndSaveData = async () => {
        if (!this.validate()) return;

        const createSchema = {
            title: 'Process Details',
            type: 'object',
            required: ['processTitle', 'steps'],
            properties: {
                processTitle: {
                    type: 'string',
                    title: 'Process Title',
                    default: this.state.processTitle,
                },
                steps: {
                    type: 'array',
                    title: 'Steps',
                    items: { type: 'string' },
                    default: this.state.selectedSteps.map((s, i) => `${i + 1}. ${s.label}`),
                },
            },
        };

        const uiSchema = {
            'ui:submitButtonOptions': { norender: true },
            processTitle: { 'ui:widget': 'textarea', 'ui:disabled': true },
            steps: { 'ui:disabled': true, items: { 'ui:readonly': true } },
        };

        if (this.state.saveFlag) {
            await this.addFormHandler(
                this.state.processId,
                this.state.processTitle,
                createSchema,
                uiSchema
            );
        }
    };

    addFormHandler = async (processId, processTitle, formSchema, uiSchema) => {
        if (window.confirm('Are you sure you want to save the process?')) {
            let steps = [];
            for (let s of this.state.selectedSteps) {
            let stepId = s.value;
            let stepLabel = s.label;
            let stepdurationDays = s.durationDays;

            // If it's a new step
            if (s.meta.custom === true) {
                try {
                const res = await createStep({}, {
                    stepId: null,
                    stepTitle: stepLabel,
                    durationDays: stepdurationDays || 1,
                });
                stepId = res?.data?.result?._id;
                } catch (err) {
                console.error("Error creating step", stepLabel, err);
                continue;
                }
            }

            steps.push({
                stepId : stepId,
                stepTitle: stepLabel,
                durationDays: stepdurationDays,
            });
            }
            const payload = {
                processTitle,
                // numberOfSteps: this.state.selectedSteps.length,
                processSteps: steps,
                formSchema,
                uiSchema,
            };
            try {
                if (!processId) {
                    await createStdProcess({}, payload);
                    ShowToast(
                        'Process created successfully',
                        TYPE_SUCCESS,
                        INTERVAL,
                        this.props,
                        '/std-process-page'
                    );
                } else {
                    await updateStdProcess({}, payload, processId);
                    ShowToast(
                        'Process updated successfully',
                        TYPE_SUCCESS,
                        INTERVAL,
                        this.props,
                        '/std-process-page'
                    );
                }
            } catch (error) {
                console.error(
                    `Error occurred while ${
                        processId ? 'Updating' : 'Creating'
                    } the Process`,
                    error
                );
            }
        }
    };

    cancelHandler = () => this.props.history.push('/std-process-page');

    render() {
        if (this.state.isLoading) return <Spinner />;
        const { error } = this.state;
        return (
            <div className="main-content">
                <div className="pagetitle row" style={{ color: 'black', paddingTop: 15, marginTop: '-25px' }}>
                    <div className="col-md-10 sm_device">
                        <i className={`${FILE_ICON}`}></i> Standard Process Creation
                    </div>
                    <div className="col-md-2 sm_device" style={{ textAlign: 'right' }}>
                        {moment().format('DD MMM YYYY')}
                    </div>
                </div>
                <div style={{ borderBottom: '1px solid lightgray', marginBottom: 15, marginTop: 15 }} />
                <div className="list-wrapper">
                    <div className="mt-4 border list-table-div" style={{ marginBottom: 50, paddingTop: 20 }}>
                        <div className="App">
                            <div className="row">
                                <div className="col-md-8">
                                    <h4 className="pagetitle">
                                        <b>
                                            {this.state.editData === null
                                                ? 'Create New Process'
                                                : 'Edit Process'}
                                        </b>
                                    </h4>
                                    <br />
                                    <label>Process Title : </label>
                                    <br />
                                    <textarea
                                        rows={2}
                                        cols={80}
                                        style={{ maxWidth: '100%', border: '1px solid lightgrey', padding: '2px' }}
                                        value={this.state.processTitle || ''}
                                        onChange={(e) => this.setProcessTitle(e.target.value)}
                                        placeholder="Enter process title"
                                    />
                                    <div className="text-danger error-msg">{error.stepTitle}</div>
                                </div>
                            </div>
                            <div className="pagetitle row" style={{ color: 'black', paddingTop: 15 }}>
                                <div className="col-md-10 sm_device">
                                    <i className={`${STEPS_ICON}`}></i> Step Creation
                                </div>
                            </div>
                            <div style={{ borderBottom: '1px solid lightgray', marginBottom: 15, marginTop: 0 }} />
                            <div className="step-add-div">
                                <div className="row">
                                    <div className="col-md-5 step-select" style={{ textAlign: 'center' }}>
                                        <label><b>Step List</b></label>
                                    </div>
                                    <div className="col-md-3 step-duration" style={{ textAlign: 'center' }}>
                                        <label>Duration (in days) <span className="text-danger">*</span> :</label>
                                    </div>
                                    <div className="col-md-3 step-actions" style={{ textAlign: 'center' }}>
                                        <label><b>Actions</b></label>
                                    </div>
                                </div>
                                {this.state.stepListComponent.length === 0 && (
                                    <div className="text-warning mb-2">No steps available from server.</div>
                                )}
                                {this.state.selectedSteps.map((stepItem, index) => {
                                    const option = stepItem.value ? { value: stepItem.value, label: stepItem.label, meta: stepItem.meta } : null;
                                    return (
                                        <div key={`selectedStep-${index}`}>
                                            <div className="row mt-0  step-row" style={{ cursor: 'default', paddingTop: '10px' }}
                                                title="Drag Step"
                                                onDragEnter={(e) => this.dragEnter(e, index)}
                                                onDragOver={(e) => e.preventDefault()}
                                            >
                                                <label className="margin-top-5 step-label">Step {index + 1}</label>
                                                <div className="col-md-5">
                                                <CreatableSelect
                                                    placeholder="Select or type step name"
                                                    value={option}
                                                    options={this.state.stepListComponent}
                                                    isSearchable
                                                    onChange={(e) => this.setStepSelect(e, index)}
                                                    onCreateOption={(inputValue) => {
                                                        const newOption = { value: inputValue, label: inputValue, meta: { custom: true } };

                                                        this.setState((prevState) => {
                                                            const updatedSteps = [...prevState.selectedSteps];
                                                            updatedSteps[index] = {
                                                                value: newOption.value,
                                                                label: newOption.label,
                                                                meta: newOption.meta,
                                                                durationDays: 1,
                                                            };

                                                            return {
                                                                stepListComponent: [...prevState.stepListComponent, newOption],
                                                                selectedSteps: updatedSteps,
                                                            };
                                                        });
                                                    }}
                                                    menuPortalTarget={isBrowser ? document.body : null}
                                                    menuPosition={isBrowser ? 'fixed' : 'absolute'}
                                                    styles={{
                                                        menuPortal: (base) => ({
                                                            ...base,
                                                            zIndex: 99999,
                                                        }),
                                                    }}
                                                    getOptionLabel={(opt) => opt?.label ?? ''}
                                                    getOptionValue={(opt) => String(opt?.value ?? '')}
                                                    noOptionsMessage={() => 'Type to create a step'}
                                                />
                                                </div>
                                                <span className="text-danger"> *</span>
                                                <div className="col-md-3">
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        step="1"
                                                        className="form-control brand-font placeholderColor"
                                                        style={{ padding: "1.9rem", fontSize: "1.3rem" }}
                                                        value={stepItem.durationDays}
                                                        onChange={(e) => this.setDuration(index, e.target.value)}
                                                        placeholder="Enter number of days"
                                                    />
                                                </div>
                                                <div className="col-md-1 step-actions">
                                                    <i className={`${ARROW_DOWN_ICON} icon-style`} style={{ cursor: 'pointer' }}
                                                        onClick={() => this.arrowDown(index)}
                                                        title="Move step down"
                                                    />
                                                    <i className={`${ARROW_UP_ICON} icon-style`} style={{ cursor: 'pointer' }}
                                                        onClick={() => this.arrowUp(index)}
                                                        title="Move step up"
                                                    />
                                                    <i className={`${PLUS_ICON} icon-style`} style={{ cursor: 'pointer' }}
                                                        onClick={this.addStep}
                                                        title="Add new step"
                                                    />
                                                    <i className={`${TRASH_ICON} icon-style`} style={{ cursor: 'pointer' }}
                                                        onClick={() => this.removeStep(index)}
                                                        title="Remove step"
                                                    />
                                                    <i className={`${ALIGNJUSTIFY_ICON} icon-style`} style={{ cursor: 'grab' }}
                                                        title="Drag step"
                                                        draggable
                                                        onDragStart={(e) => this.dragStart(e, index)}
                                                        onDragEnd={this.drop}
                                                    />
                                                </div>
                                            </div>
                                            {(!stepItem || !stepItem.value) && (
                                                <div className="text-danger error-msg">{error[`selectedSteps_${index}`]}</div>
                                            )}
                                        </div>
                                    );
                                })}
                                <div className="text-danger error-msg">{error.selectedStep}</div>
                            </div>
                            <div className="row mt-4 mb-5">
                                <div className="col-md-8">
                                    <button className="brand-button ml-5" onClick={this.saveData}>Save</button>
                                    <button className="cancel_button ml-5" onClick={this.cancelHandler}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default CreateStdProcessPage;